import React, { useState } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
 *  TABS THEME — edit here, every tabbed page in the project changes.
 *  Same shape as TABLE_THEME and BREADCRUMB_THEME: nothing visual is
 *  hard-coded below. Keep values as complete literal Tailwind class names.
 * ═══════════════════════════════════════════════════════════════════════════ */
export const TABS_THEME = {
  /* ── The bar ────────────────────────────────────────────────────────────── */
  barBg:         'bg-white',
  barBorder:     'border border-gray-200',
  barRadius:     'rounded-xl',
  barShadow:     'shadow-sm',
  barPadding:    'px-2',
  barScroll:     'flex items-stretch gap-1 overflow-x-auto',

  /* ── A tab ──────────────────────────────────────────────────────────────── */
  tabPadding:    'px-3.5 py-3',
  tabSize:       'text-xs',
  tabWeight:     'font-medium',
  tabColor:      'text-gray-500',
  tabHover:      'hover:text-gray-800',
  tabRadius:     'rounded-lg',
  tabTransition: 'transition-colors',

  /* ── The active tab ─────────────────────────────────────────────────────── */
  tabActiveColor:  'text-primary-700',
  tabActiveWeight: 'font-semibold',
  tabActiveBg:     'bg-primary-50',

  /* ── The underline on the active tab ────────────────────────────────────── */
  indicator:       'bg-primary-600',
  indicatorHeight: 'h-0.5',

  /* ── Icon ───────────────────────────────────────────────────────────────── */
  iconGap:       'gap-2',
};

const cx = (...p) => p.filter(Boolean).join(' ');

/**
 * Tabs — the common tab bar.
 *
 * Drop-in for the MUI TabbedPage tab strip, same tab shape:
 *   tabs = [{ key, label, icon, content }]
 *
 * Controlled:   <Tabs tabs={tabs} active={key} onChange={setKey} />
 * Uncontrolled: <Tabs tabs={tabs} defaultTab="periods" />  (renders content too)
 *
 * The existing components/TabbedPage/TabbedPage.js is untouched — the nine
 * other payroll pages keep working as they are.
 */
export default function Tabs({
  tabs = [],
  active: activeProp,
  onChange,
  defaultTab,
  renderContent = true,
  theme,
  className = '',
}) {
  const t = { ...TABS_THEME, ...theme };
  const [inner, setInner] = useState(defaultTab || tabs[0]?.key);
  const isControlled = activeProp !== undefined;
  const active = isControlled ? activeProp : inner;
  const activeTab = tabs.find((x) => x.key === active) || tabs[0];

  const select = (key) => {
    if (!isControlled) setInner(key);
    onChange?.(key);
  };

  return (
    <>
      <div className={cx(t.barBg, t.barBorder, t.barRadius, t.barShadow, t.barPadding, className)}>
        <div className={t.barScroll}>
          {tabs.map((tab) => {
            const on = tab.key === activeTab?.key;
            return (
              <button
                key={tab.key}
                onClick={() => select(tab.key)}
                className={cx(
                  'relative flex items-center whitespace-nowrap shrink-0',
                  t.iconGap, t.tabPadding, t.tabSize, t.tabRadius, t.tabTransition,
                  on
                    ? cx(t.tabActiveColor, t.tabActiveWeight, t.tabActiveBg)
                    : cx(t.tabColor, t.tabWeight, t.tabHover)
                )}
              >
                {tab.icon}
                {tab.label}
                {on && (
                  <span className={cx('absolute left-2 right-2 bottom-0 rounded-full',
                    t.indicator, t.indicatorHeight)} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {renderContent && activeTab && <div className="mt-4">{activeTab.content}</div>}
    </>
  );
}